import {CampaignRecord,PageState,PageName,ColumnIds,ResetPageDto,CLinkOwnerOption, QueryString, IncludeExclude, DemandSourcesResponseDto, PublisherOrSiteOption, PublisherRecord, ResponseStatus} from "../utils/types";
import {getGroupByItemsPerPage} from "../utils/misc";
import StateHelper from "../utils/StateHelper";
import {IdName} from "../utils/types";
import dayjs from "../utils/dayjs";
import * as H from 'history';
import { DemandSources } from "../services/DemandSources";
import { Dispatch, SetStateAction } from "react";

let _history:H.History<H.LocationState> = null;
let _basePath:string = null;

function getValidGroupByNamesFromState(currentStateString : string, page : string) : string {
  const validGroupbyElements : IdName[] = getGroupByItemsPerPage(page);
  
  const validNames : string[] = validGroupbyElements.map( (element : IdName) => element.id);

  let stateGroupbyElements : string[] = currentStateString.split(",");

  let validElements = stateGroupbyElements.filter( (e : string) => validNames.includes(e));

  const value = validElements.join(",");

  return value;
}


export const PagesHelper = {
  setHistory(historyObject:H.History<H.LocationState>) {
    _history = historyObject;
  },

  setBasePath(basePath: string) {
    _basePath = basePath;
  },

  getBasePath(): string {
    return _basePath;
  },

  historyPushFromPageState: function () {
    const pageState : PageState = StateHelper.getPageState();
    const newUrl = this.getPageUrl(pageState.page, pageState);
    _history.push(newUrl);
  },

  historyReplaceFromPageState: function () {
    const pageState : PageState = StateHelper.getPageState();
    const newUrl = this.getPageUrl(pageState.page, pageState);
    _history.replace(newUrl);
  },

  historyPush: function(pageUrl: string) {
    _history.push(pageUrl);
  },

  getPageUrl: function (page : string, pageState : PageState): string {
    let url = "/" + page + "?";

    // Full filtering for products and performanceStats pages
    if (page !== PageName.products && page !== PageName.performanceStats) {
      // viewAs only for other pages (trackingLinksLookup, lookup, etc.)
      if (pageState.viewAs !== null) {
        url += "viewAs=" + encodeURIComponent(pageState.viewAs);
      }
      return url;
    }

    // Use the page state as-is to preserve filters when navigating between pages
    // If groupBy is null (never set), use the default for this page
    const stateWithDefaults = {...pageState};
    if (stateWithDefaults.groupBy === null) {
      stateWithDefaults.groupBy = this.getDefaultGroupByForPage(page);
    }

    url += this.getCommonUrlSearch(stateWithDefaults);

    if (stateWithDefaults.countries != null && stateWithDefaults.countries.length) {
      url += `&countries${stateWithDefaults.countriesIncludeExclude.toString()}=${stateWithDefaults.countries}`;
    }

    if (stateWithDefaults.productIds !== null && stateWithDefaults.productIds.length > 0) {
      url += `&products${stateWithDefaults.productsIncludeExclude.toString()}=${stateWithDefaults.productIds}`;
    }

    if (stateWithDefaults.demandSourceIds !== null && stateWithDefaults.demandSourceIds.length > 0) {
      url += `&demandSourceIds${stateWithDefaults.demandSourceIdsIncludeExclude?.toString() || 'Include'}=${stateWithDefaults.demandSourceIds}`;
    }

    if (stateWithDefaults.campaignIds !== null && stateWithDefaults.campaignIds.length > 0) {
      url += `&campaignIds${stateWithDefaults.campaignIdsIncludeExclude?.toString() || 'Include'}=${stateWithDefaults.campaignIds}`;
    }

    if (stateWithDefaults.queryFlags != null && stateWithDefaults.queryFlags.length > 0 ) {
      url += "&queryFlags=" + Array.from(stateWithDefaults.queryFlags).join(",");
    }

    if (stateWithDefaults.cLinks && stateWithDefaults.cLinks.length > 0 ) {
      url += `&clinks${stateWithDefaults.cLinksIncludeExclude.toString()}=${stateWithDefaults.cLinks.join(",")}`;
    }

    if (stateWithDefaults.viewAs !== null) {
      url += "&viewAs=" + encodeURIComponent(stateWithDefaults.viewAs);
    }

    if (stateWithDefaults.pivotBy !== null) {
      url += "&pivotBy=" + stateWithDefaults.pivotBy;
    }

    // Add view mode to URL (only for performance stats page)
    if (page === PageName.performanceStats && stateWithDefaults.performanceStatsViewMode !== null) {
      url += "&viewMode=" + stateWithDefaults.performanceStatsViewMode;
    }

    return url;
  },

  getInitialPageUrl(page : string) : string {
    // Only products, performanceStats, and overall pages use date/groupBy URL parameters
    // Other pages (lookup, trackingLinksLookup, liveLogs, inventory, authUser) manage their own state
    if (page !== PageName.products && page !== PageName.performanceStats && page !== PageName.overall) {
      return "";
    }

    // Default date range: today only for products page, last 10 days for performance page
    let dateStart = dayjs.utc().format("YYYY-MM-DD");
    let dateEnd = dayjs.utc().format("YYYY-MM-DD");

    if ( page === PageName.performanceStats ){
      // Performance page: default to last 10 days (10 days total)
      dateStart = dayjs.utc().subtract(9, 'days').format("YYYY-MM-DD");
    }

    let url = "?dateStart=" + dateStart;
    url += "&dateEnd=" + dateEnd;

    if ( page === PageName.products ){
      url += "&groupBy=" + ColumnIds.productID;
    }
    else if ( page === PageName.performanceStats ){
      // Performance Stats page: get raw daily data, we'll pivot on client side
      url += "&groupBy=" + ColumnIds.date;
      url += "&dateMode=days";
      url += "&viewMode=trafficSource"; // Default to traffic source view
    }
    return url;
  },

  getDefaultGroupByForPage(page: string): string {
    if (page === PageName.products) {
      return ColumnIds.productID;
    } else if (page === PageName.performanceStats) {
      // Performance Stats view groups by date (we pivot on client side)
      return ColumnIds.date;
    } else {
      return ColumnIds.apptapPublisherID;
    }
  },

  getCommonUrlSearch : function(state : PageState) {
    let url = "dateStart=" + dayjs.utc(state.dateStart).format("YYYY-MM-DD");
    url += "&dateEnd=" + dayjs.utc(state.dateEnd).format("YYYY-MM-DD");

    // Traffic sources and placements parameters
    // Only send placementIds OR publisherIds/siteIds, not both
    if (state.placementIds != null && state.placementIds.length > 0) {
      url += `&placementIds${state.placementsIncludeExclude.toString()}=${state.placementIds.join(",")}`;
    } else {
      if (state.publisherIds != null && state.publisherIds.length > 0) {
        url += `&publisherIds${state.trafficSourcesIncludeExclude.toString()}=${state.publisherIds.join(",")}`;
      }
      if (state.siteIds != null && state.siteIds.length > 0) {
        url += `&siteIds${state.trafficSourcesIncludeExclude.toString()}=${state.siteIds.join(",")}`;
      }
    }

    if (state.trafficTypes != null) {
      url += `&trafficTypes${state.trafficTypesIncludeExclude.toString()}=${state.trafficTypes}`;
    }

    if (state.groupBy != null) {
      url += "&groupBy=" + state.groupBy;
    }

    return url;
  },

  getQueryParameters(page : string, includeRequestId = true) : QueryString {
    const pageState:PageState = StateHelper.getPageState(page);
    const queryParams:QueryString = {
      dateStart: dayjs.utc(pageState.dateStart).format("YYYY-MM-DD"),
      dateEnd: dayjs.utc(pageState.dateEnd).format("YYYY-MM-DD")
    };

    if (pageState.groupBy !== null) {
      queryParams.groupBy = getValidGroupByNamesFromState(pageState.groupBy, page);
    }

    // Traffic sources and placements parameters
    // Only send placementIds OR publisherIds/siteIds, not both
    if (pageState.placementIds != null && pageState.placementIds.length > 0) {
      (queryParams as any)['placementIds' + pageState.placementsIncludeExclude.toString()] = pageState.placementIds.join(",");
    } else {
      if (pageState.publisherIds != null && pageState.publisherIds.length > 0) {
        (queryParams as any)['publisherIds' + pageState.trafficSourcesIncludeExclude.toString()] = pageState.publisherIds.join(",");
      }
      if (pageState.siteIds != null && pageState.siteIds.length > 0) {
        (queryParams as any)['siteIds' + pageState.trafficSourcesIncludeExclude.toString()] = pageState.siteIds.join(",");
      }
    }

    if (pageState.trafficTypes !== null) {
      (queryParams as any)['trafficTypes' + pageState.trafficTypesIncludeExclude.toString()] = pageState.trafficTypes;
    }

    if (pageState.countries !== null && pageState.countries.length) {
      (queryParams as any)['countries' + pageState.countriesIncludeExclude.toString()] = pageState.countries;
    }
    if (pageState.queryFlags != null && pageState.queryFlags.length > 0) {
      queryParams.queryFlags = Array.from(pageState.queryFlags).join(",");
    }

    if (pageState.cLinks !== null) {
      (queryParams as any)['clinks' + pageState.cLinksIncludeExclude.toString()] = pageState.cLinks.join(",");
    }
    if (pageState.productIds !== null && pageState.productIds.length > 0)  {
      (queryParams as any)['products' + pageState.productsIncludeExclude.toString()] = pageState.productIds;
    }

    if (pageState.demandSourceIds !== null && pageState.demandSourceIds.length > 0) {
      (queryParams as any)['demandSourceIds' + (pageState.demandSourceIdsIncludeExclude.toString() || 'Include')] = pageState.demandSourceIds;
    }

    if (pageState.campaignIds !== null && pageState.campaignIds.length > 0) {
      (queryParams as any)['campaignIds' + (pageState.campaignIdsIncludeExclude?.toString() || 'Include')] = pageState.campaignIds;
    }

    return queryParams;
  },

  clearState : function (pageState : PageState){
    pageState.countries = null;
    pageState.dateEnd = null;
    pageState.dateStart = null;
    pageState.groupBy = null;
    pageState.publisherIds = null;
    pageState.siteIds = null;
    pageState.placementIds = null;
    pageState.trafficTypes = null;
    pageState.campaignIds = null;
    pageState.demandSourceIds = null;
    pageState.productIds = null;
    pageState.cLinks = null;
    pageState.queryFlags = null;
    pageState.pivotBy = null;
  },

  getCampaignSourceAndCampaignID : function(c : CampaignRecord): string {
    return (c.demandSourceId == null ? "***" : c.demandSourceId) + ":" + (c.campaignId == null ? "***" : c.campaignId);
  },
  fromCLinkOwnerOptionToString : function(c : CLinkOwnerOption): string {
    return c.record instanceof Object ? c.record.cLinkId : c.record;
  },
  getDemandSourceLabel: function(demandSourceId: string, includeSourceId: boolean = true) {
    const demandSourcesState = StateHelper.getDemandSourcesState();
    if (!demandSourcesState) return demandSourceId;

    const demandSource = demandSourcesState.find((c) => c.demandSourceId === demandSourceId);
    if (!demandSource) return demandSourceId;

    const { demandSourceName, demandSourcePartnerName } = demandSource;

    if (!demandSourceName) return demandSourceId;

    const label = includeSourceId
      ? `${demandSourceName} (${demandSourceId})`
      : demandSourceName;

    return demandSourcePartnerName
      ? `${demandSourcePartnerName}: ${label}`
      : label;
  },

  getTrafficSourceLabel: function(publisherId: string, siteId: string, trafficSources?: PublisherOrSiteOption[]) {
    if (!trafficSources || !publisherId) {
      return `${publisherId}:${siteId}`;
    }
    
    const trafficSource = trafficSources.find((t) => t.record && t.record.apptapPublisherId === publisherId);
    const publisherRecord: PublisherRecord = trafficSource && trafficSource.record ? trafficSource.record as PublisherRecord : null;
    
    if (publisherRecord) {
      if (siteId) {
        const publisherSiteRecord = publisherRecord.publisherSites.find((publisherSite) => publisherSite.apptapSiteId === siteId);
        return publisherSiteRecord && publisherSiteRecord.apptapPublisherSiteName 
          ? `${publisherRecord.apptapPublisherName.trim()}: ${publisherSiteRecord.apptapPublisherSiteName.trim()}`
          : publisherRecord.apptapPublisherName;
      } else {
        return publisherRecord.apptapPublisherName;
      }
    }
    
    // Fallback to raw format if not found
    return `${publisherId}:${siteId}`;
  },

  resetFilters : function ( page : string, resetPageDto : ResetPageDto, searchFunction : any) : void {
    try{

      const pageState : PageState = StateHelper.getPageState();

      if( resetPageDto.trafficSourcesRef ){
        resetPageDto.trafficSourcesRef.current.clear();
      }

      if( resetPageDto.countriesRef ) {
        resetPageDto.countriesRef.current.clear();
      }
      if( resetPageDto.trafficTypesRef ){
        resetPageDto.trafficTypesRef.current.clear();
      }

      if( resetPageDto.groupByRef ){
        resetPageDto.groupByRef.current.clear();
      }
      if( resetPageDto.placementsRef ){
        resetPageDto.placementsRef.current.clear();
      }

      if( resetPageDto.productsRef !== null && resetPageDto.productsRef.current !== null) {
        resetPageDto.productsRef.current.clear();
      }
      if (resetPageDto.campaignsRef !== null && resetPageDto.campaignsRef.current !== null) {
        resetPageDto.campaignsRef.current.clear();
      }
      if (resetPageDto.cLinksRef !== null && resetPageDto.cLinksRef.current !== null) {
        resetPageDto.cLinksRef.current.clear();
      }
      if (resetPageDto.queryFlagsRef !== null && resetPageDto.queryFlagsRef.current !== null) {
        resetPageDto.queryFlagsRef.current.clear();
      }
      PagesHelper.clearState( pageState );

      pageState.groupBy = null;

      pageState.dateStart = dayjs.utc();
      pageState.dateEnd = dayjs.utc();
      pageState.trafficSourcesIncludeExclude = IncludeExclude.Include;
      pageState.placementsIncludeExclude = IncludeExclude.Include;
      pageState.countriesIncludeExclude = IncludeExclude.Include;
      pageState.campaignIdsIncludeExclude = IncludeExclude.Include;
      pageState.demandSourceIdsIncludeExclude = IncludeExclude.Include;
      pageState.productsIncludeExclude = IncludeExclude.Include;
      pageState.trafficTypesIncludeExclude = IncludeExclude.Include;
      pageState.cLinksIncludeExclude = IncludeExclude.Include;

      searchFunction();
    }
    catch( e ){
      console.log(e);
    }

  },
  loadDemandSourcesHelper: async(getDemandSourcesAbortController: AbortController, setDemandSourcesResponseDto?: Dispatch<SetStateAction<DemandSourcesResponseDto | null>>) => {
    let demandSourcesState = StateHelper.getDemandSourcesState();
    if (demandSourcesState.length === 0) {
      demandSourcesState = await DemandSources.getDemandSources(getDemandSourcesAbortController, setDemandSourcesResponseDto);
      StateHelper.setDemandSourcesState(demandSourcesState);
    } else if (setDemandSourcesResponseDto) {
      // If demand sources are already in state, still update the response DTO for the component
      setDemandSourcesResponseDto({
        status: ResponseStatus.OK,
        error: null,
        demandSourcesResponse: demandSourcesState,
      });
    }
    // save demand sources into localstorage for angularjs brandsLookupApp and clinkManager
    if (demandSourcesState) {
      const demandSources = demandSourcesState.map((c) => c.demandSourceId);
      const clinkManagerLocalStorage = JSON.parse(StateHelper.getLocalStorageItem('ngStorage-clinkManager')) || {};
      clinkManagerLocalStorage["demandSources"] = demandSources;
      StateHelper.setLocalStorageItem('ngStorage-demandSources', JSON.stringify(demandSources));
      StateHelper.setLocalStorageItem('ngStorage-clinkManager', JSON.stringify(clinkManagerLocalStorage));
    }
  }
};